/*
  cart-period-guard.js
  ------------------------------------------------------------------
  決済ボタンが押された「その瞬間」に、カート内商品の販売期間を再チェックする。

  背景（注文#5968 / 2026-09-01）:
    商品ページ・カートページの販売期間判定はLiquid（サーバー側）で正しく動作しているが、
    Shopifyはレンダリング結果をキャッシュするため、締切直後の一定時間は
    「締切前に生成されたHTML」がそのまま配信されることがある。
    実測で、締切から約22分後までカート追加が発生していた。
    ページ生成時の判定だけでは、この経路を塞げない。

  仕組み:
    1. button[name="checkout"] のクリックをキャプチャ段階で横取り
    2. /cart?view=periodcheck&_=<timestamp> を叩き、サーバーで今この瞬間の判定を取得
       （クエリを毎回変えることでキャッシュを回避）
    3. 期間外の商品があれば決済へ進ませず、カート画面にメッセージと削除リンクを表示
    4. 問題なければそのまま決済へ

  方針:
    - 通信失敗・タイムアウト時は「通す」（フェイルオープン）。
      その場合はページ生成時のLiquid判定＝従来どおりの挙動に戻るだけで、悪化させない。
    - 対象は カートページ(main-cart) と ミニカート(mini-cart) の両方。

  関連: templates/cart.periodcheck.liquid（判定を返すエンドポイント）
*/
(function () {
  'use strict';

  var ENDPOINT = '/cart?view=periodcheck';
  var TIMEOUT_MS = 5000;
  var NOTICE_CLASS = 'cart-period-guard__notice';

  /* --------------------------------------------------
     サーバーへ現時点の判定を取りに行く
     -------------------------------------------------- */
  function fetchStatus() {
    var url = ENDPOINT + '&_=' + Date.now();
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;

    return fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) throw new Error('status ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var data = JSON.parse(text.trim());
        if (!data || data.ok !== true) throw new Error('unexpected payload');
        return data;
      });
  }

  /* --------------------------------------------------
     期間外商品のメッセージを表示する
     -------------------------------------------------- */
  function buildNotice(items) {
    var wrap = document.createElement('div');
    wrap.className = 'alert alert--error ' + NOTICE_CLASS;
    wrap.setAttribute('role', 'alert');
    wrap.style.marginBottom = '12px';

    var p = document.createElement('p');
    p.style.margin = '0 0 8px';
    p.innerHTML = 'カート内に<strong>販売期間が終了した商品</strong>が含まれています。'
                + '該当商品を削除してから決済へお進みください。';
    wrap.appendChild(p);

    var ul = document.createElement('ul');
    ul.style.margin = '0';
    ul.style.paddingLeft = '1.2em';

    items.forEach(function (it) {
      var li = document.createElement('li');
      li.textContent = it.title + '（' + (it.before ? '9:00販売開始前' : '販売終了') + '）　';
      var a = document.createElement('a');
      a.className = 'link';
      a.href = '/cart/change?quantity=0&line=' + it.line;
      a.textContent = '削除';
      li.appendChild(a);
      ul.appendChild(li);
    });

    wrap.appendChild(ul);
    return wrap;
  }

  function showNotice(button, items) {
    document.querySelectorAll('.' + NOTICE_CLASS).forEach(function (el) { el.remove(); });

    var notice = buildNotice(items);
    // カートページは専用の告知枠がある。無ければボタンの直前に差し込む。
    var slot = document.querySelector('.cart-recap__notices');
    if (slot && slot.offsetParent !== null) {
      slot.insertBefore(notice, slot.firstChild);
    } else if (button && button.parentNode) {
      button.parentNode.insertBefore(notice, button);
    } else {
      return;
    }
    notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function disableButton(button) {
    if (!button) return;
    button.disabled = true;
    button.classList.add('button--disabled');
    button.classList.remove('button--primary');
    button.innerHTML = '決済へ進めません<br>（期間外商品があります）';
  }

  /* --------------------------------------------------
     決済ボタンのクリックを横取り
     -------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('button[name="checkout"]');
    if (!button || button.disabled) return;

    // 検証済みの再クリックはそのまま通す
    if (button.dataset.periodGuard === 'passed') return;

    event.preventDefault();
    event.stopPropagation();

    var label = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    fetchStatus()
      .then(function (data) {
        var bad = (data.items || []).filter(function (it) { return it.ended || it.before; });

        if (data.blocked && bad.length) {
          button.removeAttribute('aria-busy');
          showNotice(button, bad);
          disableButton(button);
          return;
        }

        // 問題なし → 検証済みフラグを立てて、本来の送信を実行
        button.dataset.periodGuard = 'passed';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
        button.click();
      })
      .catch(function () {
        // 通信失敗時は従来どおりの挙動に戻す（フェイルオープン）
        button.dataset.periodGuard = 'passed';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
        button.click();
      });
  }, true);

  /* --------------------------------------------------
     カートを開いた時点でも先回りでチェックしておく
     （動的決済ボタン等、クリックを横取りできない導線への保険）
     -------------------------------------------------- */
  function precheck() {
    if (!document.querySelector('button[name="checkout"]')) return;

    fetchStatus()
      .then(function (data) {
        var bad = (data.items || []).filter(function (it) { return it.ended || it.before; });
        if (!data.blocked || !bad.length) return;

        document.querySelectorAll('button[name="checkout"]').forEach(function (btn) {
          showNotice(btn, bad);
          disableButton(btn);
        });
        // Shop Pay 等の動的決済ボタンも隠す
        document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
          el.style.display = 'none';
        });
      })
      .catch(function () { /* 失敗時は従来挙動のまま */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', precheck);
  } else {
    precheck();
  }

  // ミニカートは後から開かれるので、開いた時にも走らせる
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (t && t.closest && t.closest('[data-action="toggle-mini-cart"]')) {
      setTimeout(precheck, 300);
    }
  });
})();
